import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { documentService } from '@services/document.service';
import { useAppDispatch } from '@store/index';
import { showNotification } from '@features/ui/uiSlice';
import { apiErrorStatus, resolveApiErrorMessage } from '@utils/apiErrorMessages';

type DownloadDocument = (documentId: string, fallbackName: string, version?: number) => Promise<void>;

// Module scope: shared by every caller and survives unmount (inactive Radix tabs unmount).
// Value: whether a repeat click on that download has already been told.
const inFlight = new Map<string, boolean>();

/**
 * Downloads a document version and shows a toast on failure; never rejects. A repeat
 * call for the same document and version while one is in flight only says so, once.
 */
export function useDocumentDownload(): DownloadDocument {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  return useCallback<DownloadDocument>(
    async (documentId, fallbackName, version) => {
      const key = `${documentId}:${version ?? 'current'}`;
      if (inFlight.has(key)) {
        if (!inFlight.get(key)) {
          inFlight.set(key, true);
          dispatch(showNotification({ type: 'info', messageKey: 'documents.downloadInProgress' }));
        }
        return;
      }
      inFlight.set(key, false);
      try {
        await documentService.downloadDocument(documentId, fallbackName, version);
      } catch (error) {
        dispatch(showNotification({ type: 'error', message: failureMessage(t, error) }));
      } finally {
        inFlight.delete(key);
      }
    },
    [dispatch, t],
  );
}

// HTTP_404 and ETIMEDOUT have no shared message key; a download gives them their own.
function failureMessage(t: TFunction, error: unknown): string {
  if (apiErrorStatus(error) === 404) return t('documents.downloadNotFound');
  if ((error as { code?: string } | null)?.code === 'ETIMEDOUT') return t('documents.downloadTimedOut');
  return resolveApiErrorMessage(t, error, { fallbackKey: 'documents.downloadFailed' });
}
